import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faGamepad, faGlobe, faMicrochip, faTrophy } from '@fortawesome/free-solid-svg-icons';

const chapters = [
  {
    id: 'origins',
    range: '1972-1983',
    label: 'Stage 01',
    title: 'The living room becomes an arcade',
    intro: 'Before sprawling worlds and online lobbies, a few glowing blocks on a television were enough to make the future feel interactive.',
    image: '/images/2026/history/era-1972-1983.png',
    milestones: [
      { year: '1972', title: 'Home gaming begins', copy: 'The Magnavox Odyssey turns the television into something people can play, establishing the basic idea of a home console.' },
      { year: '1977', title: 'Cartridges change the rules', copy: 'Atari 2600 popularises interchangeable cartridges. One machine can now hold an expanding library instead of a handful of built-in games.' },
      { year: '1980', title: 'Arcade heroes come home', copy: 'Home versions of arcade hits make recognisable games and high-score chasing part of everyday family life.' },
      { year: '1983', title: 'The market crashes', copy: 'Too many systems and rushed releases damage confidence in North America. Gaming needs a reset, and one is already on the way.' }
    ]
  },
  {
    id: 'sixteen-bit',
    range: '1985-1994',
    label: 'Stage 02',
    title: 'Nintendo, Sega and the 16-bit war',
    intro: 'Games become faster, more colourful and more characterful. The console itself becomes part of your identity.',
    image: '/images/2026/history/era-1985-1994.png',
    milestones: [
      { year: '1985', title: 'Nintendo rebuilds the console market', copy: 'The NES arrives with tight quality control, distinctive hardware and games designed around memorable characters and precise play.' },
      { year: '1988', title: 'The Mega Drive brings speed', copy: 'Sega pushes arcade energy, sports games and an older attitude into the home, beginning one of gaming’s defining rivalries.' },
      { year: '1989', title: 'Gaming leaves the house', copy: 'Game Boy proves that portable hardware can create lasting adventures, link players together and survive years of daily use.' },
      { year: '1990', title: 'The 16-bit era peaks', copy: 'The Super Nintendo expands colour, sound and storytelling while developers learn how much personality can fit inside a cartridge.' }
    ]
  },
  {
    id: 'three-d',
    range: '1995-2006',
    label: 'Stage 03',
    title: 'Discs, 3D worlds and online play',
    intro: 'A new dimension changes cameras, controls and storytelling. Games begin to look and sound more like the entertainment worlds we know today.',
    image: '/images/2026/history/era-1995-2006.png',
    milestones: [
      { year: '1995', title: 'PlayStation changes the audience', copy: 'CD storage, 3D graphics and confident marketing push games into music, fashion and mainstream youth culture.' },
      { year: '1996', title: 'Analogue control unlocks 3D', copy: 'New controller designs make movement through open 3D spaces feel natural, creating a template that still shapes games.' },
      { year: '2001', title: 'Three very different futures', copy: 'PlayStation 2, Xbox and GameCube compete through enormous libraries, powerful hardware, online ambition and unmistakable personalities.' },
      { year: '2004', title: 'Online communities take hold', copy: 'Broadband services, voice chat and persistent player identities turn the console into a place where friendships and rivalries continue nightly.' }
    ]
  },
  {
    id: 'modern',
    range: '2007-NOW',
    label: 'Final Stage',
    title: 'Everyone plays, everywhere',
    intro: 'Motion control, digital stores, handheld hybrids, streaming and gaming PCs widen the idea of where a game lives and who gets to play it.',
    image: '/images/2026/history/era-2007-now.png',
    milestones: [
      { year: '2007', title: 'Gaming reaches a wider audience', copy: 'Motion controls and accessible social games bring new players into the room while HD consoles make cinematic worlds the norm.' },
      { year: '2013', title: 'Libraries move online', copy: 'Downloads, updates and subscription services reshape how games are bought, maintained and discovered across a console generation.' },
      { year: '2017', title: 'Console and handheld become one', copy: 'Nintendo Switch makes full-scale gaming portable without abandoning television play, changing expectations around flexibility.' },
      { year: '2020+', title: 'Fast storage and limitless formats', copy: 'Current consoles slash loading times while gaming PCs, cloud play, remasters and retro hardware let every generation exist at once.' }
    ]
  }
];

const History = () => (
  <div className="content-page history-2026 history-expanded">
    <section className="history-title-screen">
      <div className="history-title-copy">
        <p className="pixel-kicker">Game On Archives</p>
        <h1>Press start through gaming history</h1>
        <p>From two paddles and a glowing square to handheld worlds, online communities and custom gaming PCs: this is how gaming kept levelling up.</p>
        <a className="pixel-button" href="#origins">Start timeline <FontAwesomeIcon icon={faArrowRight} /></a>
      </div>
      <div className="history-stats" aria-label="Timeline overview">
        <div><FontAwesomeIcon icon={faGamepad} /><strong>50+</strong><span>years of home gaming</span></div>
        <div><FontAwesomeIcon icon={faTrophy} /><strong>4</strong><span>defining eras</span></div>
        <div><FontAwesomeIcon icon={faGlobe} /><strong>Billions</strong><span>of players worldwide</span></div>
      </div>
    </section>

    <nav className="era-select" aria-label="Jump to a gaming era">
      <span>Select stage</span>
      {chapters.map((chapter) => <a href={`#${chapter.id}`} key={chapter.id}>{chapter.range}</a>)}
    </nav>

    <div className="history-chapters">
      {chapters.map((chapter, chapterIndex) => (
        <section className="history-chapter" id={chapter.id} key={chapter.id}>
          <div className="chapter-art">
            <img src={chapter.image} alt={`Pixel art celebrating gaming from ${chapter.range}`} loading="lazy" decoding="async" />
            <div className="chapter-art-label"><span>{chapter.label}</span><strong>{chapter.range}</strong></div>
          </div>
          <header className="chapter-heading">
            <div><p className="pixel-kicker">{chapter.label}</p><h2>{chapter.title}</h2></div>
            <p>{chapter.intro}</p>
          </header>
          <div className="milestone-grid">
            {chapter.milestones.map((item, index) => (
              <article className="history-milestone" key={`${chapter.id}-${item.year}`}>
                <div className="milestone-number">{String(chapterIndex * 4 + index + 1).padStart(2, '0')}</div>
                <span>{item.year}</span>
                <h3>{item.title}</h3>
                <p>{item.copy}</p>
              </article>
            ))}
          </div>
        </section>
      ))}
    </div>

    <section className="history-legacy">
      <div>
        <p className="pixel-kicker">New Game+</p>
        <h2>Every generation is still playable</h2>
        <p>That is the best part of gaming history: it does not stay behind glass. Old cartridges, restored consoles, remasters, backwards compatibility and modern PCs keep decades of ideas alive.</p>
      </div>
      <div className="legacy-actions">
        <Link className="pixel-button" to="/contact">Ask what is in store <FontAwesomeIcon icon={faArrowRight} /></Link>
        <Link className="legacy-link" to="/pc-building"><FontAwesomeIcon icon={faMicrochip} /> Jump to modern PCs</Link>
      </div>
    </section>
  </div>
);

export default History;
